import { useEffect, useRef, useCallback } from "react";

interface WheelPickerProps {
  items: string[];
  selectedIndex: number;
  onChange: (index: number) => void;
  itemHeight?: number;
}

export default function WheelPicker({
  items,
  selectedIndex,
  onChange,
  itemHeight = 44,
}: WheelPickerProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startScrollTop = useRef(0);
  const lastY = useRef(0);
  const velocity = useRef(0);
  const rafRef = useRef(0);
  const ignoreScrollRef = useRef(false);

  const VISIBLE = 5;
  const containerH = itemHeight * VISIBLE;
  const paddingItems = Math.floor(VISIBLE / 2);

  const scrollToIndex = useCallback(
    (idx: number, smooth = true) => {
      const el = listRef.current;
      if (!el) return;
      const target = idx * itemHeight;
      if (smooth) {
        el.style.transition = "transform 0.25s cubic-bezier(0.25,0.46,0.45,0.94)";
      } else {
        el.style.transition = "none";
      }
      el.style.transform = `translateY(${-target}px)`;
    },
    [itemHeight]
  );

  useEffect(() => {
    scrollToIndex(selectedIndex, false);
  }, [selectedIndex, scrollToIndex]);

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

  const getIndexFromTransform = useCallback(() => {
    const el = listRef.current;
    if (!el) return selectedIndex;
    const match = el.style.transform.match(/translateY\((-?\d+(?:\.\d+)?)px\)/);
    if (!match) return selectedIndex;
    const y = parseFloat(match[1]);
    return clamp(Math.round(-y / itemHeight), 0, items.length - 1);
  }, [itemHeight, items.length, selectedIndex]);

  const snapToNearest = useCallback(() => {
    const idx = getIndexFromTransform();
    scrollToIndex(idx, true);
    onChange(idx);
  }, [getIndexFromTransform, scrollToIndex, onChange]);

  const applyDelta = useCallback(
    (deltaY: number) => {
      const el = listRef.current;
      if (!el) return;
      const match = el.style.transform.match(/translateY\((-?\d+(?:\.\d+)?)px\)/);
      const current = match ? parseFloat(match[1]) : -selectedIndex * itemHeight;
      const maxScroll = (items.length - 1) * itemHeight;
      const next = clamp(current - deltaY, -maxScroll, 0);
      el.style.transition = "none";
      el.style.transform = `translateY(${next}px)`;
    },
    [itemHeight, items.length, selectedIndex]
  );

  // Touch events
  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      cancelAnimationFrame(rafRef.current);
      isDragging.current = true;
      startY.current = e.touches[0].clientY;
      lastY.current = e.touches[0].clientY;
      velocity.current = 0;
      const el = listRef.current;
      if (el) {
        const match = el.style.transform.match(/translateY\((-?\d+(?:\.\d+)?)px\)/);
        startScrollTop.current = match ? parseFloat(match[1]) : -selectedIndex * itemHeight;
        el.style.transition = "none";
      }
    },
    [selectedIndex, itemHeight]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const currentY = e.touches[0].clientY;
      velocity.current = currentY - lastY.current;
      lastY.current = currentY;
      const deltaY = startY.current - currentY;
      const el = listRef.current;
      if (!el) return;
      const maxScroll = (items.length - 1) * itemHeight;
      const next = clamp(startScrollTop.current - deltaY, -maxScroll, 0);
      el.style.transform = `translateY(${next}px)`;
    },
    [itemHeight, items.length]
  );

  const onTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const el = listRef.current;
    if (!el) return;

    // 惯性滑动
    let vel = velocity.current;
    const maxScroll = (items.length - 1) * itemHeight;

    const inertia = () => {
      if (Math.abs(vel) < 0.5) {
        snapToNearest();
        return;
      }
      const match = el.style.transform.match(/translateY\((-?\d+(?:\.\d+)?)px\)/);
      const current = match ? parseFloat(match[1]) : 0;
      const next = clamp(current + vel, -maxScroll, 0);
      el.style.transition = "none";
      el.style.transform = `translateY(${next}px)`;
      vel *= 0.92;
      rafRef.current = requestAnimationFrame(inertia);
    };
    rafRef.current = requestAnimationFrame(inertia);
  }, [itemHeight, items.length, snapToNearest]);

  // Mouse events
  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      cancelAnimationFrame(rafRef.current);
      isDragging.current = true;
      startY.current = e.clientY;
      lastY.current = e.clientY;
      velocity.current = 0;
      const el = listRef.current;
      if (el) {
        const match = el.style.transform.match(/translateY\((-?\d+(?:\.\d+)?)px\)/);
        startScrollTop.current = match ? parseFloat(match[1]) : -selectedIndex * itemHeight;
        el.style.transition = "none";
      }
    },
    [selectedIndex, itemHeight]
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      velocity.current = e.clientY - lastY.current;
      lastY.current = e.clientY;
      const deltaY = startY.current - e.clientY;
      const el = listRef.current;
      if (!el) return;
      const maxScroll = (items.length - 1) * itemHeight;
      const next = clamp(startScrollTop.current - deltaY, -maxScroll, 0);
      el.style.transform = `translateY(${next}px)`;
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      const el = listRef.current;
      if (!el) return;
      let vel = velocity.current;
      const maxScroll = (items.length - 1) * itemHeight;
      const inertia = () => {
        if (Math.abs(vel) < 0.5) { snapToNearest(); return; }
        const match = el.style.transform.match(/translateY\((-?\d+(?:\.\d+)?)px\)/);
        const current = match ? parseFloat(match[1]) : 0;
        const next = clamp(current + vel, -maxScroll, 0);
        el.style.transition = "none";
        el.style.transform = `translateY(${next}px)`;
        vel *= 0.92;
        rafRef.current = requestAnimationFrame(inertia);
      };
      rafRef.current = requestAnimationFrame(inertia);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [itemHeight, items.length, snapToNearest]);

  // Wheel scroll
  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      cancelAnimationFrame(rafRef.current);
      applyDelta(-e.deltaY * 0.5);
      clearTimeout((onWheel as unknown as { _t?: ReturnType<typeof setTimeout> })._t);
      (onWheel as unknown as { _t?: ReturnType<typeof setTimeout> })._t = setTimeout(snapToNearest, 150);
    },
    [applyDelta, snapToNearest]
  );

  return (
    <div
      className="relative overflow-hidden select-none"
      style={{ height: containerH, cursor: "grab", touchAction: "none" }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onWheel={onWheel}
    >
      {/* 选中高亮条 */}
      <div
        className="absolute left-0 right-0 pointer-events-none z-10"
        style={{
          top: paddingItems * itemHeight,
          height: itemHeight,
          background: "rgba(180,0,10,0.08)",
          borderTop: "1px solid rgba(180,0,10,0.25)",
          borderBottom: "1px solid rgba(180,0,10,0.25)",
        }}
      />
      {/* 上下渐变遮罩 */}
      <div
        className="absolute inset-x-0 top-0 pointer-events-none z-20"
        style={{
          height: paddingItems * itemHeight,
          background: "linear-gradient(to bottom, rgba(255,249,240,0.95), rgba(255,249,240,0.3))",
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none z-20"
        style={{
          height: paddingItems * itemHeight,
          background: "linear-gradient(to top, rgba(255,249,240,0.95), rgba(255,249,240,0.3))",
        }}
      />

      {/* 列表 */}
      <div
        ref={listRef}
        style={{
          paddingTop: paddingItems * itemHeight,
          willChange: "transform",
          transform: `translateY(${-selectedIndex * itemHeight}px)`,
        }}
      >
        {items.map((item, i) => (
          <div
            key={i}
            className="flex items-center justify-center text-sm font-medium"
            style={{
              height: itemHeight,
              color: i === selectedIndex ? "#8b1a1a" : "#999",
              fontWeight: i === selectedIndex ? 700 : 400,
              fontSize: i === selectedIndex ? "15px" : "13px",
              fontFamily: "'Noto Serif SC', serif",
              transition: "color 0.15s, font-size 0.15s",
            }}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}
